import React, { useMemo } from 'react';
import { Table, TableBody, TableFooter, TableHeader, TableRow, TableCell } from './ui/table';
import { flexRender } from '@tanstack/react-table';
import TanstackPagination from './TanstackPagination';
import { ArrowLongDownIcon, ArrowLongUpIcon } from '@heroicons/react/24/outline';
import { tanstackTableTheme } from '../contexts/ThemeContext';
import TableSkeleton from './TableSkeleton';
import constants from '../commonUtils/constants';

// Import DnD Kit dependencies
import {
    DndContext,
    KeyboardSensor,
    MouseSensor,
    TouchSensor,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
    arrayMove,
    SortableContext,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Drag handle cell component
const RowDragHandleCell = ({ rowId }) => {
    const { attributes, listeners } = useSortable({
        id: rowId,
    });

    return (
        <button 
            {...attributes} 
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 rounded hover:bg-transparent text-gray-500"
            type="button"
        >
            <svg xmlns="http://www.w3.org/2000/svg" width="15px" height="15px" viewBox="0 0 24 24" fill="none">
                <path d="M5 10H19M14 19L12 21L10 19M14 5L12 3L10 5M5 14H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>

        </button>
    );
};

// Draggable row component
const DraggableRow = ({ row, children }) => {
    const { transform, transition, setNodeRef, isDragging } = useSortable({
        id: row.id,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition: transition,
        opacity: isDragging ? 0.8 : 1,
        zIndex: isDragging ? 1 : 0,
        position: 'relative',
    };

    return (
        <TableRow ref={setNodeRef} style={style} key={row.id}>
            {children}
        </TableRow>
    );
};

const ReorderTable = ({
    loading,
    table,
    data,
    columns,
    pagination,
    handlePageChange,
    notfoundMsg = "No data found",
    // New props for drag and drop
    enableRowReordering = false,
    onRowReorder, // Callback function to handle reordered data
    getRowId, // Function to get unique ID for each row
    dragHandleColumn = null // Optional: custom drag handle column config
}) => {

    // Create data IDs for DnD
    const dataIds = useMemo(() => {
        if (!enableRowReordering || !data) return [];
        return data.map((item, index) => getRowId ? getRowId(item) : index.toString());
    }, [data, enableRowReordering, getRowId]);

    // DnD sensors
    const sensors = useSensors(
        useSensor(MouseSensor, {}),
        useSensor(TouchSensor, {}),
        useSensor(KeyboardSensor, {})
    );

    // Handle drag end
    function handleDragEnd(event) {
        const { active, over } = event;
        if (active && over && active.id !== over.id) {
            const oldIndex = dataIds.indexOf(active.id);
            const newIndex = dataIds.indexOf(over.id);
            const reorderedData = arrayMove(data, oldIndex, newIndex);
            
            // Call the callback with reordered data
            if (onRowReorder) {
                onRowReorder(reorderedData);
            }
        }
    }

    if (loading) {
        return <TableSkeleton columns={columns.length} rows={constants.tableUI.defaultPerpage} headers={columns.map(col => col.header)} />;
    }

    // Create drag handle column if reordering is enabled and no custom column is provided
    const dragColumn = dragHandleColumn || {
        id: 'drag-handle',
        header: ' ',
        cell: ({ row }) => <RowDragHandleCell rowId={row.id} />,
        size: 60,
    };

    const tableContent = (
        <Table className={`rounded-md overflow-hidden border`}>
            <TableHeader>
                {table.getHeaderGroups().map(headerGroup => (
                    <TableRow key={headerGroup.id} className={`${tanstackTableTheme}`}>
                        {/* Add drag handle header if reordering is enabled */}
                        {enableRowReordering && (
                            <TableCell key="drag-handle" className="font-bold text-center w-16">
                                {dragColumn.header}
                            </TableCell>
                        )}
                        {headerGroup.headers.map(header => (
                            <TableCell key={header.id} className="font-bold cursor-pointer text-center" onClick={header.column.getToggleSortingHandler()}>
                                {flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                )}
                                {{
                                    asc: <ArrowLongUpIcon className="ml-1 h-5 w-4 inline" />,
                                    desc: <ArrowLongDownIcon className="ml-1 h-5 w-4 inline" />,
                                }[header.column.getIsSorted()] ?? null}
                            </TableCell>
                        ))}
                    </TableRow>
                ))}
            </TableHeader>
            <TableBody>
                {data && data.length === 0 ? (
                    <TableRow>
                        <TableCell colSpan={columns.length + (enableRowReordering ? 1 : 0)} className="text-center">
                            {notfoundMsg}
                        </TableCell>
                    </TableRow>
                ) : enableRowReordering ? (
                    <SortableContext
                        items={dataIds}
                        strategy={verticalListSortingStrategy}
                    >
                        {table.getRowModel().rows.map(row => (
                            <DraggableRow key={row.id} row={row}>
                                {/* Drag handle cell */}
                                <TableCell key={`${row.id}-drag-handle`} className="text-center w-16">
                                    <RowDragHandleCell rowId={row.id} />
                                </TableCell>
                                {/* Regular cells */}
                                {row.getVisibleCells().map(cell => (
                                    <TableCell key={cell.id} className="text-center">
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </TableCell>
                                ))}
                            </DraggableRow>
                        ))}
                    </SortableContext>
                ) : (
                    table.getRowModel().rows.map(row => (
                        <TableRow key={row.id}>
                            {row.getVisibleCells().map(cell => (
                                <TableCell key={cell.id} className="text-center">
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))
                )}
            </TableBody>
            {pagination && (
                <TableFooter className="bg-transparent hover:bg-transparent">
                    <TableRow>
                        <TableCell colSpan={99}>
                            <TanstackPagination
                                table={table}
                                data={data}
                                pagination={pagination}
                                handlePageChange={handlePageChange}
                            />
                        </TableCell>
                    </TableRow>
                </TableFooter>
            )}
        </Table>
    );

    // Wrap with DndContext if reordering is enabled
    if (enableRowReordering) {
        return (
            <DndContext
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis]}
                onDragEnd={handleDragEnd}
                sensors={sensors}
            >
                {tableContent}
            </DndContext>
        );
    }

    return tableContent;
};

export default ReorderTable;